import React from 'react';
import { NoData, SoldToAddress, SoldToCompanyName } from '@css-xp/ppc-common';
import classNames from 'classnames';
import { FormattedDate } from '@css-xp/base/formattedV2';
import { Order, OrderLineItem, InvoiceGroup } from '@css-xp/graphtypes/src/generated-types-new';
import { CloudProductDetailView, OrderTypeView } from '@css-xp/base/entitlement-utils';
import { NEW_DATE_TIME_FORMAT } from '@atlassian/cssxp-typescommon';
import { getRouteUrl } from '@css-xp/ppc-utils';
import { routePaths } from '@css-xp/ppc-constants';
import { CloudSiteCell } from './cloud-site-cell';
import { CloudProductView } from './cloud-product-view';
import { OrderEmailCell } from './order-email-cell';
import { OrderSummaryCell } from './order-summary-cell';
import { OrderHistoryTableColumns } from '../types';

export interface OrderLineItemRowProps {
  testId?: string;
  transactionAccountId: string;
  lineItem: OrderLineItem;
  purchaseMetadata: any;
  order: Order;
  invoiceGroup?: InvoiceGroup;
  isFlatDiscountSupportEnabled?: boolean;
  visibleColumns?: string[];
  subscriptionCellVersion?: number;
  shouldShowLegacyInvoiceId?: boolean;
  isApp?: boolean;
  hasIntegratedApps?: boolean;
  shouldTopAlignIcon?: boolean;
  isSandboxSupportForSubscriptionsEnabled?: boolean;
  isManualBillingSupportEnabled?: boolean;
  isGracePeriodVisibilityEnabled?: boolean;
}

export const OrderLineItemRow: React.FC<OrderLineItemRowProps> = ({
  testId = 'ppc-order-history-line-item-row',
  transactionAccountId,
  lineItem,
  purchaseMetadata,
  order,
  invoiceGroup,
  isFlatDiscountSupportEnabled,
  visibleColumns,
  subscriptionCellVersion,
  shouldShowLegacyInvoiceId,
  isApp = false,
  hasIntegratedApps = false,
  shouldTopAlignIcon = false,
  isSandboxSupportForSubscriptionsEnabled = false,
  isManualBillingSupportEnabled = false,
  isGracePeriodVisibilityEnabled = false,
}) => {
  const {
    entitlementId,
    entitlementNumber = '',
    productId,
    productName,
    edition,
    logoId,
    quoteLineItemDetailsReference,
    isExternallyBilled: isExternallyBilledLineItem,
    offering,
  } = lineItem || {};

  const { quoteId, quoteLineItemId } = quoteLineItemDetailsReference || {};

  const { created, metadata, contextSubject, billTo } = order;
  const { supportTicketNumber } = metadata || {};
  const subscriptionsRoutePPC =
    transactionAccountId && getRouteUrl(routePaths.subscriptions, { txaId: transactionAccountId });
  const isSandboxEntitlement = (isSandboxSupportForSubscriptionsEnabled && lineItem?.isSandbox) ?? false;
  const isAddOn = !!lineItem?.isAddOn;
  const isExternallyBilled = isManualBillingSupportEnabled && !isAddOn && !isApp && isExternallyBilledLineItem;

  const rowData = visibleColumns?.map((column) => {
    switch (column) {
      case OrderHistoryTableColumns.SUBSCRIPTION:
        return (
          <td>
            {subscriptionCellVersion === 2 ? (
              <CloudProductDetailView
                productId={productId as string}
                productName={productName}
                productLink={`${subscriptionsRoutePPC}${entitlementId}`}
                logoId={logoId as string}
                version={2}
                entitlementNumber={entitlementNumber as string}
                shouldHideExtraInfo={!isSandboxEntitlement}
                pricingPlan={lineItem?.pricingPlan}
                edition={edition ?? offering?.name}
                shouldTopAlignIcon={shouldTopAlignIcon}
                shouldHideEntitlementDetails={isApp}
                isSandbox={isSandboxEntitlement}
                isAddOn={isAddOn}
                isExternallyBilled={isExternallyBilled}
              />
            ) : (
              <CloudProductView
                productId={productId as string}
                entitlementNumber={entitlementNumber as string}
                logoId={logoId as string}
                productName={productName}
                subscriptionRoute={`${subscriptionsRoutePPC}${entitlementId}`}
                isSandbox={isSandboxEntitlement}
                isAddOn={isAddOn}
                isExternallyBilled={isExternallyBilled}
              />
            )}
          </td>
        );

      case OrderHistoryTableColumns.PLAN:
        return <td>{edition ?? offering?.name ?? <NoData />}</td>;

      case OrderHistoryTableColumns.ORDER_DATE:
        return (
          <td>
            <FormattedDate date={created} dateFormat={NEW_DATE_TIME_FORMAT} />
          </td>
        );

      case OrderHistoryTableColumns.COMPANY:
        return (
          <td>
            <SoldToCompanyName txaId={transactionAccountId} companyName={invoiceGroup?.shipToParty?.name} />
          </td>
        );

      case OrderHistoryTableColumns.SOLD_TO:
        return (
          <td>
            <SoldToAddress txaId={transactionAccountId} shipTo={invoiceGroup?.shipToParty} />
          </td>
        );

      case OrderHistoryTableColumns.SITE:
        return isApp ? (
          <td data-testid="empty-site-cell"></td>
        ) : (
          <CloudSiteCell entitlementId={entitlementNumber as string} transactionAccountId={transactionAccountId} />
        );

      case OrderHistoryTableColumns.ORDER_SUMMARY:
        return (
          <td>
            {lineItem ? (
              <OrderSummaryCell
                orderLineItem={lineItem}
                billTo={billTo}
                isFlatDiscountSupportEnabled={isFlatDiscountSupportEnabled}
                quoteId={quoteId}
                quoteLineItemId={quoteLineItemId}
                transactionAccountId={transactionAccountId}
                shouldShowLegacyInvoiceId={shouldShowLegacyInvoiceId}
                isGracePeriodVisibilityEnabled={isGracePeriodVisibilityEnabled}
              />
            ) : (
              <NoData />
            )}
          </td>
        );

      case OrderHistoryTableColumns.ORDER_TYPE:
        return (
          <td>
            {lineItem ? (
              <OrderTypeView
                orderLineItem={lineItem}
                billTo={billTo}
                isFlatDiscountSupportEnabled={isFlatDiscountSupportEnabled}
                isGracePeriodVisibilityEnabled={isGracePeriodVisibilityEnabled}
              />
            ) : (
              <NoData />
            )}
          </td>
        );

      case OrderHistoryTableColumns.NOTE:
        return (
          <td>
            <span className="line-clamp">{purchaseMetadata?.note || <NoData />}</span>
          </td>
        );

      case OrderHistoryTableColumns.SUPPORT_TICKET:
        return <td>{supportTicketNumber || <NoData />}</td>;

      case OrderHistoryTableColumns.PLACED_BY:
      case OrderHistoryTableColumns.CREATED_BY:
        return (
          <td>
            <OrderEmailCell email={contextSubject} />
          </td>
        );
    }
  });

  return (
    <tr
      data-testid={testId}
      className={classNames('white-row', {
        'order-row-v2': shouldTopAlignIcon,
        'order-row-app': isApp,
        'order-row-parent-with-apps': hasIntegratedApps,
      })}
    >
      {rowData}
    </tr>
  );
};
